import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Paper,
  Typography,
  Stack,
  Box,
  Button,
  Checkbox,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Link,
  MenuItem,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { API_BASE_URL } from "../config";
import {
  CERT_QUALIFICATIONS,
  CLOTHING_SIZES,
  DECLARATIONS,
  DOCTOR_GRADES,
  DRIVING_LICENCE_QUALIFICATION,
  EXPIRY_QUALIFICATIONS,
  HCP_QUALIFICATIONS,
  GMC_QUALIFICATIONS,
  INDEMNITY_PROVIDERS,
  SAS_DOCTOR,
  VEHICLES,
} from "../constants";
import { useMyPersonnel } from "../useMyPersonnel";
import { notify } from "../notify";
import { useWriteLock } from "../useWriteLock";
import DocumentLink from "../components/DocumentLink";
import DocumentUpload from "../components/DocumentUpload";
import {
  CHANGE_FIELD_LABELS, GMC_RE, NMC_RE, fmtDate, fmtDateTime, hcpcRegex, regInput,
} from "../personnelChanges";

const API = `${API_BASE_URL}/personnel`;
const READINESS_API = `${API}/self/readiness`;
const NO_READINESS = { availability_fields: [], contract_fields: [], contract_pending: false };

// An input the record needs and doesn't yet have (core/personnel_readiness.py).
const NEED_SX = {
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main", borderWidth: 2 },
  "& .MuiInputLabel-root": { color: "warning.dark" },
  "& .MuiFormHelperText-root": { color: "warning.dark" },
};
const QUAL_CERT_API = `${API_BASE_URL}/qualification-certificates`;
const CONTACT_EMAIL = "hello@shelter-stone.com";

// Which qualifications drive which conditional fields (mirrors PersonnelDialog,
// and core/personnel_self_edit.py on the server, which ignores a field the
// record's qualifications don't show).
const isDoctor = (q) => q.includes("Doctor") || q.includes("Doctor (training)");
// Doctors and physician associates: a GMC number (the grade is doctors' only).
const isGmc = (q) => GMC_QUALIFICATIONS.some(g => q.includes(g));
const isNurse = (q) => q.includes("Nurse");
const isHcpcPro = (q) => q.includes("Paramedic") || q.includes("Physiotherapist");

const hasVal = (v) => !(v === null || v === undefined || v === "");

// The fields a person may change here — the server's PersonnelSelfUpdate. Name,
// email, sign-in address, roles and qualifications are not among them, nor is
// anything this page doesn't show.
const EDITABLE = Object.keys(CHANGE_FIELD_LABELS);
const DOC_FIELDS = ["indemnity_certificate_id", "cv_document_id",
                    "driving_licence_front_id", "driving_licence_back_id"];

const formFrom = (p) => Object.fromEntries(EDITABLE.map(k => [
  k, k === "independent_prescriber" ? !!p[k] : (p[k] ?? (DOC_FIELDS.includes(k) ? null : "")),
]));

// A label/value pair rendered into the enclosing definition-list grid.
function Row({ label, value }) {
  return (
    <Box sx={{ display: "contents" }}>
      <Box component="dt" sx={{ fontWeight: 600, color: "text.secondary" }}>{label}</Box>
      <Box component="dd" sx={{ m: 0 }}>{value}</Box>
    </Box>
  );
}

// Section heading + a two-column definition list; renders nothing when it has no rows.
function Section({ title, rows }) {
  const shown = rows.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <>
      <Divider textAlign="left">{title}</Divider>
      <Box
        component="dl"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" },
          columnGap: 2, rowGap: 1, m: 0,
        }}
      >
        {shown.map(([label, value]) => <Row key={label} label={label} value={value} />)}
      </Box>
    </>
  );
}

// Section heading + form fields, in edit mode.
function EditSection({ title, children }) {
  return (
    <>
      <Divider textAlign="left">{title}</Divider>
      <Stack spacing={2}>{children}</Stack>
    </>
  );
}

// Side by side on a wide screen, stacked on a phone.
function FieldRow({ children }) {
  return <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>{children}</Stack>;
}

// Chips for a list value (roles, qualifications).
function Chips({ items }) {
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
      {items.map((it) => <Chip key={it} size="small" label={it} />)}
    </Stack>
  );
}

export default function MyDetails() {
  const { loading, person, hasIdentity, email } = useMyPersonnel();
  // The record as last saved here; until then, as loaded.
  const [saved, setSaved] = useState(null);
  const [qualCerts, setQualCerts] = useState([]);
  const [certsLoaded, setCertsLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pending, setPending] = useState([]); // own changes awaiting review
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  // Documents uploaded in this edit: a pointer field's new document, and a
  // qualification's new certificate. Only the id is sent; these are for the links.
  const [docs, setDocs] = useState({});
  const [newCerts, setNewCerts] = useState({});
  // A certificate's expiry as typed, by qualification; sent only where it differs.
  const [certExpiries, setCertExpiries] = useState({});
  // The declarations of suitability as ticked in the form, by record field.
  const [declared, setDeclared] = useState({});
  // Which fields availability (always) and a contract (once one is waiting for
  // them) need, to highlight while they're empty.
  const [readiness, setReadiness] = useState(NO_READINESS);
  const { busy: saving, run: runWrite } = useWriteLock();

  const p = saved || person;

  const loadCerts = (id) => axios.get(QUAL_CERT_API + "/", { params: { personnel_id: id } })
    .then(res => setQualCerts(res.data)).catch(() => setQualCerts([]))
    .finally(() => setCertsLoaded(true));
  const loadPending = (id) => axios.get(`${API}/${id}/changes`)
    .then(res => setPending(res.data)).catch(() => setPending([]));
  const loadReadiness = () => axios.get(READINESS_API)
    .then(res => setReadiness(res.data)).catch(() => setReadiness(NO_READINESS));

  useEffect(() => {
    if (!person?.id) { setQualCerts([]); setPending([]); return; }
    loadCerts(person.id);
    loadPending(person.id);
    loadReadiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.id]);

  const startEdit = () => {
    setForm(formFrom(p));
    setDocs({
      indemnity_certificate_id: p.indemnity_certificate || null,
      cv_document_id: p.cv_document || null,
      driving_licence_front_id: p.driving_licence_front || null,
      driving_licence_back_id: p.driving_licence_back || null,
    });
    setNewCerts({});
    setCertExpiries(Object.fromEntries(qualCerts.map(qc => [qc.qualification, qc.expiry || ""])));
    setDeclared(Object.fromEntries(DECLARATIONS.map(d => [d.field, !!p[d.field]])));
    setEditing(true);
  };

  // ?edit=1 (the "My details" button beside a list of missing details on My
  // events) opens straight into the edit view, where those fields are
  // highlighted. Waits for the certificates, which seed the form's expiries.
  const wantEdit = searchParams.get("edit") === "1";
  useEffect(() => {
    if (!wantEdit || !p || !certsLoaded) return;
    setSearchParams({}, { replace: true });
    if (!editing) startEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantEdit, p, certsLoaded]);

  if (loading) {
    return <Stack sx={{ alignItems: "center", py: 6 }}><CircularProgress /></Stack>;
  }

  if (!hasIdentity) {
    return (
      <Stack spacing={3}>
        <Typography variant="h4">My details</Typography>
        <Alert severity="info">You are not signed in, so there is no personal record to show.</Alert>
      </Stack>
    );
  }

  if (!p) {
    return (
      <Stack spacing={3}>
        <Typography variant="h4">My details</Typography>
        <Alert severity="warning">
          We couldn't find a Personnel record matching your account
          {email ? ` (${email})` : ""}. Please ask an administrator to add you.
        </Alert>
      </Stack>
    );
  }

  const quals = p.qualifications || [];
  const indemnityProvider =
    p.indemnity_provider === "Other" ? p.indemnity_provider_other : p.indemnity_provider;
  const qualsDisplay = quals.map(q => (q === "Other" && p.qualifications_other ? `Other: ${p.qualifications_other}` : q));
  const isHcp = quals.some(q => HCP_QUALIFICATIONS.includes(q));
  const hasLicence = quals.includes(DRIVING_LICENCE_QUALIFICATION);
  const certQuals = CERT_QUALIFICATIONS.filter(q => quals.includes(q));
  const fullName = [p.forename, p.surname].filter(Boolean).join(" ");

  // ---------------------------------------------------------------- view mode
  if (!editing) {
    // Certificates on file (qualification certs plus the indemnity certificate).
    const certRows = qualCerts
      .filter(qc => qc.document || (qc.expiry && EXPIRY_QUALIFICATIONS.includes(qc.qualification)))
      .map(qc => [qc.qualification, (
        <Stack key={qc.id} direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          {qc.document ? <DocumentLink doc={qc.document} /> : <span>No certificate uploaded</span>}
          {qc.expiry && EXPIRY_QUALIFICATIONS.includes(qc.qualification) && (
            <Typography variant="body2" color="text.secondary">expires {fmtDate(qc.expiry)}</Typography>
          )}
        </Stack>
      )]);

    const contact = [
      ["Name", fullName],
      hasVal(p.email) && ["Email", p.email],
      hasVal(p.login_email) && ["Sign-in address", p.login_email],
      hasVal(p.phone) && ["Phone", p.phone],
      hasVal(p.date_of_birth) && ["Date of birth", fmtDate(p.date_of_birth)],
    ];

    const roleQual = [
      p.roles?.length > 0 && ["Shelter Stone role", <Chips items={p.roles} />],
      quals.length > 0 && ["Qualifications", <Chips items={qualsDisplay} />],
      quals.includes("RRMT") && ["MRT team", p.mrt_team || "—"],
    ];

    const registration = [];
    if (isGmc(quals)) registration.push(["GMC number", p.gmc_number || "—"]);
    if (isDoctor(quals)) {
      registration.push(["Grade", p.doctor_grade
        ? `${p.doctor_grade}${p.doctor_grade_date ? ` (since ${fmtDate(p.doctor_grade_date)})` : ""}`
        : "—"]);
    }
    if (isNurse(quals)) {
      registration.push(["NMC PIN", p.nmc_pin || "—"]);
    }
    if (isHcpcPro(quals)) {
      registration.push(["HCPC number", p.hcpc_number || "—"]);
    }
    if (isNurse(quals) || quals.includes("Paramedic")) {
      registration.push(["Independent prescriber", p.independent_prescriber ? "Yes" : "No"]);
    }

    const indemnity = isHcp ? [
      hasVal(indemnityProvider) && ["Provider", indemnityProvider],
      hasVal(p.indemnity_expiry) && ["Expiry", fmtDate(p.indemnity_expiry)],
      p.indemnity_certificate && ["Certificate", <DocumentLink doc={p.indemnity_certificate} />],
    ] : [];

    const address = [
      hasVal(p.address_line1) && ["Address line 1", p.address_line1],
      hasVal(p.address_line2) && ["Address line 2", p.address_line2],
      hasVal(p.town) && ["Town", p.town],
      hasVal(p.county) && ["County", p.county],
      hasVal(p.postcode) && ["Postcode", p.postcode],
    ];

    const other = [
      hasVal(p.emergency_contact_name) && ["Emergency contact", p.emergency_contact_name],
      hasVal(p.emergency_contact_phone) && ["Emergency phone", p.emergency_contact_phone],
      hasVal(p.clothing_size) && ["Clothing size", p.clothing_size],
      hasVal(p.vehicle) && ["Vehicle", p.vehicle],
      hasVal(p.vehicle_registration) && ["Vehicle registration", p.vehicle_registration],
    ];

    // Only shown when something is on file: the licence is held behind its
    // qualification, so most people have none of this.
    const drivingLicence = [
      hasVal(p.driving_licence_number) && ["Licence number", p.driving_licence_number],
      hasVal(p.driving_licence_test_date) && ["Test date", fmtDate(p.driving_licence_test_date)],
      hasVal(p.driving_licence_expiry) && ["Expiry", fmtDate(p.driving_licence_expiry)],
      p.driving_licence_front && ["Photo (front)", <DocumentLink doc={p.driving_licence_front} />],
      p.driving_licence_back && ["Photo (back)", <DocumentLink doc={p.driving_licence_back} />],
    ];

    // The referee sits with the CV: both are what we know of someone's background.
    const cv = [
      p.cv_document && ["CV", <DocumentLink doc={p.cv_document} />],
      hasVal(p.reference_name) && ["Reference", p.reference_name],
      hasVal(p.reference_position) && ["Reference position", p.reference_position],
      hasVal(p.reference_email) && ["Reference email", p.reference_email],
    ];

    const lastPending = pending[pending.length - 1];

    return (
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h4">{fullName || "My details"}</Typography>
          <Button variant="outlined" startIcon={<EditIcon />} onClick={startEdit}>
            Edit my details
          </Button>
        </Stack>

        {lastPending && (
          <Alert severity="info">
            You updated your details on {fmtDateTime(lastPending.changed_at)}. The Shelter
            Stone team will check the changes.
          </Alert>
        )}

        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider" }}>
          <Stack spacing={2}>
            <Section title="Contact" rows={contact} />
            <Section title="Shelter Stone role & qualifications" rows={roleQual} />
            <Section title="Registration numbers" rows={registration} />
            <Section title="Indemnity" rows={indemnity} />
            <Section title="Qualification certificates" rows={certRows} />
            <Section title="Driving licence" rows={drivingLicence} />
            <Section title="CV & reference" rows={cv} />
            <Section title="Address" rows={address} />
            <Section title="Other details" rows={other} />
            <Section title="Declarations of suitability" rows={DECLARATIONS.map(d => [
              d.title, p[d.field] ? `Confirmed ${fmtDateTime(p[d.field])}` : "Not yet confirmed",
            ])} />
          </Stack>
        </Paper>
      </Stack>
    );
  }

  // ---------------------------------------------------------------- edit mode
  // --- What still needs filling in -------------------------------------------
  // The server names the fields each set of rules covers; a field is highlighted
  // while it's one of them and empty *in the form*, so the highlight goes as soon
  // as it's filled in, before saving.
  const forAvailability = new Set(readiness.availability_fields);
  const forContract = new Set(readiness.contract_pending ? readiness.contract_fields : []);
  const isEmpty = (key) => {
    const [kind, q] = key.split(/:(.*)/);
    if (kind === "certificate") {
      return !(newCerts[q] || qualCerts.find(qc => qc.qualification === q)?.document);
    }
    if (kind === "certificate_expiry") return !(certExpiries[q] ?? "");
    if (key in declared) return !declared[key];
    const v = form[key];
    return v === null || v === undefined || (typeof v === "string" && !v.trim())
      || (key === "indemnity_provider" && v === "None");
  };
  const needReason = (key) => {
    if (!isEmpty(key)) return null;
    if (forAvailability.has(key)) return "Needed to say you're available for events";
    if (forContract.has(key)) return "Needed to sign your contract";
    return null;
  };
  // Spreadable TextField props: the highlight and why, merged into any sx it has.
  const need = (key, baseSx) => {
    const reason = needReason(key);
    if (!reason) return baseSx ? { sx: baseSx } : {};
    return { sx: { ...(baseSx || {}), ...NEED_SX }, helperText: reason };
  };
  // The indemnity certificate and expiry only appear once a provider is chosen —
  // until then the provider is what's highlighted, so they aren't counted.
  const providerChosen = form.indemnity_provider && form.indemnity_provider !== "None";
  const shown = (key) => providerChosen || !["indemnity_certificate_id", "indemnity_expiry"].includes(key);
  const neededCount = [...new Set([...forAvailability, ...forContract])]
    .filter(k => shown(k) && needReason(k)).length;

  const field = (key, baseSx) => ({
    value: form[key],
    onChange: e => setForm(f => ({ ...f, [key]: e.target.value })),
    ...need(key, baseSx),
  });
  const dateProps = { type: "date", slotProps: { inputLabel: { shrink: true } } };
  const setDoc = (key) => (d) => {
    setDocs(ds => ({ ...ds, [key]: d }));
    setForm(f => ({ ...f, [key]: d.id }));
  };

  const badFormat = (val, re) => val !== "" && !re.test(val);
  const HCPC_RE = hcpcRegex(quals);
  const gmcBad = isGmc(quals) && badFormat(form.gmc_number, GMC_RE);
  const nmcBad = isNurse(quals) && badFormat(form.nmc_pin, NMC_RE);
  const hcpcBad = isHcpcPro(quals) && badFormat(form.hcpc_number, HCPC_RE);
  const indemnityActive = isHcp && form.indemnity_provider && form.indemnity_provider !== "None";
  const indemnityOk = !indemnityActive || (
    (form.indemnity_provider !== "Other" || form.indemnity_provider_other.trim()) &&
    form.indemnity_expiry && form.indemnity_certificate_id);
  const mrtOk = !quals.includes("RRMT") || form.mrt_team.trim();
  const canSave = !gmcBad && !nmcBad && !hcpcBad && indemnityOk && mrtOk;

  const save = () => runWrite(() => {
    const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
    payload.certificates = Object.fromEntries(Object.entries(newCerts).map(([q, d]) => [q, d.id]));
    const storedExpiry = (q) => qualCerts.find(qc => qc.qualification === q)?.expiry || "";
    for (const d of DECLARATIONS) payload[d.flag] = !!declared[d.field];
    payload.certificate_expiries = Object.fromEntries(Object.entries(certExpiries)
      .filter(([q, v]) => v !== storedExpiry(q))
      .map(([q, v]) => [q, v || null]));
    return axios.put(`${API}/self`, payload).then(res => {
      setSaved(res.data);
      setEditing(false);
      loadCerts(res.data.id);
      loadPending(res.data.id);
      loadReadiness();
      notify("Your details have been updated.", "success");
    });
  });

  return (
    <Stack spacing={3}>
      <Typography variant="h4">{fullName || "My details"}</Typography>

      <Alert severity="info">
        Your name, email address, sign-in address, Shelter Stone role and qualifications can't be
        changed here. If any of them need updating, please contact{" "}
        <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
        {" "}Changes you save apply straight away and are checked by the Shelter Stone team.
      </Alert>
      {neededCount > 0 && (
        <Alert severity="warning">
          The {neededCount === 1 ? "field" : `${neededCount} fields`} highlighted in amber{" "}
          {neededCount === 1 ? "is" : "are"} needed before you can say you're available for events
          {readiness.contract_pending ? " or sign your contract" : ""}.
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider" }}>
        <Stack spacing={2}>
          <Section title="Contact" rows={[
            ["Name", fullName],
            hasVal(p.email) && ["Email", p.email],
            hasVal(p.login_email) && ["Sign-in address", p.login_email],
          ]} />
          <FieldRow>
            <TextField label="Phone" fullWidth {...field("phone")} />
            <TextField label="Date of birth" {...dateProps} {...field("date_of_birth", { minWidth: 190 })} />
          </FieldRow>

          <Section title="Shelter Stone role & qualifications" rows={[
            p.roles?.length > 0 && ["Shelter Stone role", <Chips items={p.roles} />],
            quals.length > 0 && ["Qualifications", <Chips items={qualsDisplay} />],
          ]} />
          {quals.includes("RRMT") && (
            <TextField label="MRT team" fullWidth required {...field("mrt_team")}
                       error={!mrtOk} helperText={!mrtOk ? "Required for RRMT" : " "} />
          )}

          {(isGmc(quals) || isNurse(quals) || isHcpcPro(quals)) && (
            <EditSection title="Registration numbers">
              {isGmc(quals) && (
                <FieldRow>
                  <TextField label="GMC number" fullWidth value={form.gmc_number}
                             onChange={e => setForm(f => ({ ...f, gmc_number: regInput(e.target.value) }))}
                             error={gmcBad} sx={need("gmc_number").sx}
                             helperText={gmcBad ? "7 digits, optionally prefixed with 'A'."
                               : (needReason("gmc_number") || " ")} />
                  {isDoctor(quals) && (
                  <TextField label="Grade" select fullWidth {...field("doctor_grade")}
                             helperText={needReason("doctor_grade")
                               || (p.doctor_grade_date && form.doctor_grade === p.doctor_grade
                                 ? `Since ${fmtDate(p.doctor_grade_date)}` : " ")}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {DOCTOR_GRADES.map(g => (
                      <MenuItem key={g} value={g}>
                        {g === SAS_DOCTOR ? "SAS Doctor (Specialty / Specialist / Assoc. Specialist)" : g}
                      </MenuItem>
                    ))}
                  </TextField>
                  )}
                </FieldRow>
              )}
              {isNurse(quals) && (
                <TextField label="NMC PIN" fullWidth value={form.nmc_pin}
                           onChange={e => setForm(f => ({ ...f, nmc_pin: regInput(e.target.value) }))}
                           error={nmcBad} sx={need("nmc_pin").sx}
                           helperText={nmcBad ? "8 characters: digits and CAPITAL letters (e.g. 07B2988E)."
                             : (needReason("nmc_pin") || " ")} />
              )}
              {isHcpcPro(quals) && (
                <TextField label="HCPC number" fullWidth value={form.hcpc_number}
                           onChange={e => setForm(f => ({ ...f, hcpc_number: regInput(e.target.value) }))}
                           error={hcpcBad} sx={need("hcpc_number").sx}
                           helperText={hcpcBad ? "PA (paramedic) or PH (physiotherapist) followed by 5 or 6 digits."
                             : (needReason("hcpc_number") || " ")} />
              )}
              {(isNurse(quals) || quals.includes("Paramedic")) && (
                <FormControlLabel label="Independent prescriber"
                                  control={<Checkbox checked={form.independent_prescriber}
                                                     onChange={e => setForm(f => ({ ...f, independent_prescriber: e.target.checked }))} />} />
              )}
            </EditSection>
          )}

          {isHcp && (
            <EditSection title="Indemnity">
              <FieldRow>
                <TextField label="Indemnity provider" select fullWidth value={form.indemnity_provider}
                           {...need("indemnity_provider")}
                           onChange={e => setForm(f => ({
                             ...f,
                             indemnity_provider: e.target.value,
                             indemnity_provider_other: e.target.value === "Other" ? f.indemnity_provider_other : "",
                           }))}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {INDEMNITY_PROVIDERS.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </TextField>
                {indemnityActive && (
                  <TextField label="Indemnity expiry" required {...dateProps}
                             error={!form.indemnity_expiry} helperText={!form.indemnity_expiry ? "Required" : " "}
                             value={form.indemnity_expiry}
                             onChange={e => setForm(f => ({ ...f, indemnity_expiry: e.target.value }))}
                             sx={{ minWidth: 220 }} />
                )}
              </FieldRow>
              {form.indemnity_provider === "Other" && (
                <TextField label="Provider name" fullWidth required {...field("indemnity_provider_other")}
                           error={!form.indemnity_provider_other.trim()} />
              )}
              {indemnityActive && (
                <DocumentUpload doc={docs.indemnity_certificate_id} label="certificate"
                                category="indemnity_certificate" personnelId={p.id}
                                required={!form.indemnity_certificate_id}
                                onUploaded={setDoc("indemnity_certificate_id")} />
              )}
            </EditSection>
          )}

          {certQuals.length > 0 && (
            <EditSection title="Qualification certificates">
              {certQuals.map(q => {
                const current = newCerts[q] || qualCerts.find(qc => qc.qualification === q)?.document || null;
                return (
                  <Box key={q}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{q}</Typography>
                    <DocumentUpload doc={current} label={`${q} certificate`}
                                    category="qualification_certificate" personnelId={p.id}
                                    severity={needReason(`certificate:${q}`) ? "warning" : null}
                                    onUploaded={d => setNewCerts(c => ({ ...c, [q]: d }))} />
                    {/* Only the certificates that expire: the mountain awards are held for life. */}
                    {EXPIRY_QUALIFICATIONS.includes(q) && (
                      <TextField label="Expiry" size="small" {...dateProps}
                                 {...need(`certificate_expiry:${q}`, { mt: 1.5, maxWidth: 220 })}
                                 value={certExpiries[q] ?? ""}
                                 onChange={e => setCertExpiries(x => ({ ...x, [q]: e.target.value }))} />
                    )}
                  </Box>
                );
              })}
            </EditSection>
          )}

          {hasLicence && (
            <EditSection title="Driving licence">
              <FieldRow>
                <TextField label="Licence number" fullWidth value={form.driving_licence_number}
                           onChange={e => setForm(f => ({ ...f, driving_licence_number: regInput(e.target.value) }))} />
                <TextField label="Test date" {...dateProps} sx={{ minWidth: 190 }} {...field("driving_licence_test_date")} />
                <TextField label="Expiry" {...dateProps} sx={{ minWidth: 190 }} {...field("driving_licence_expiry")} />
              </FieldRow>
              <DocumentUpload doc={docs.driving_licence_front_id} label="licence photo (front)"
                              category="driving_licence_front" personnelId={p.id}
                              onUploaded={setDoc("driving_licence_front_id")} />
              <DocumentUpload doc={docs.driving_licence_back_id} label="licence photo (back)"
                              category="driving_licence_back" personnelId={p.id}
                              onUploaded={setDoc("driving_licence_back_id")} />
            </EditSection>
          )}

          <EditSection title="CV & reference">
            <DocumentUpload doc={docs.cv_document_id} label="CV" category="cv" personnelId={p.id}
                            severity={needReason("cv_document_id") ? "warning" : null}
                            onUploaded={setDoc("cv_document_id")} />
            <Box>
              <FieldRow>
                <TextField label="Reference name" fullWidth {...field("reference_name")} />
                <TextField label="Reference position" fullWidth {...field("reference_position")} />
                <TextField label="Reference email" type="email" fullWidth {...field("reference_email")} />
              </FieldRow>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, mx: 1.75 }}>
                Reference should be your educational supervisor for doctors before completing
                training; else a clinical supervisor for healthcare professionals; else someone
                who knows you professionally.
              </Typography>
            </Box>
          </EditSection>

          <EditSection title="Address">
            <TextField label="Address line 1" fullWidth {...field("address_line1")} />
            <TextField label="Address line 2" fullWidth {...field("address_line2")} />
            <FieldRow>
              <TextField label="Town" fullWidth {...field("town")} />
              <TextField label="County" fullWidth {...field("county")} />
              <TextField label="Postcode" fullWidth {...field("postcode")} />
            </FieldRow>
          </EditSection>

          <EditSection title="Other details">
            <FieldRow>
              <TextField label="Emergency contact name" fullWidth {...field("emergency_contact_name")} />
              <TextField label="Emergency contact phone" fullWidth {...field("emergency_contact_phone")} />
            </FieldRow>
            <FieldRow>
              <TextField label="Clothing size" select fullWidth {...field("clothing_size")}>
                <MenuItem value=""><em>—</em></MenuItem>
                {CLOTHING_SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <TextField label="Vehicle" select fullWidth {...field("vehicle")}>
                <MenuItem value=""><em>—</em></MenuItem>
                {VEHICLES.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
              <TextField label="Vehicle registration" fullWidth value={form.vehicle_registration}
                         onChange={e => setForm(f => ({ ...f, vehicle_registration: e.target.value.toUpperCase() }))} />
            </FieldRow>
          </EditSection>

          {/* Recorded with the time each was ticked; needed before saying you're
              available. Unticking withdraws it. */}
          <EditSection title="Declarations of suitability">
            {DECLARATIONS.map(d => {
              const reason = needReason(d.field);
              return (
                <Box key={d.field}
                     sx={{ border: "1px solid", borderColor: reason ? "warning.main" : "divider",
                           borderWidth: reason ? 2 : 1, borderRadius: 1, p: 1.5 }}>
                  <Typography variant="subtitle2">{d.title}</Typography>
                  <FormControlLabel
                    sx={{ alignItems: "flex-start", mt: 0.5, mr: 0 }}
                    control={<Checkbox checked={!!declared[d.field]} sx={{ mt: -0.75 }}
                                       onChange={e => setDeclared(x => ({ ...x, [d.field]: e.target.checked }))} />}
                    label={<Typography variant="body2">{d.text}</Typography>} />
                  <Typography variant="caption" component="div"
                              sx={{ ml: 4, color: reason ? "warning.dark" : "text.secondary" }}>
                    {reason || (p[d.field] && declared[d.field]
                      ? `Confirmed ${fmtDateTime(p[d.field])}`
                      : declared[d.field] ? "Recorded when you save" : " ")}
                  </Typography>
                </Box>
              );
            })}
          </EditSection>

          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", pt: 1 }}>
            <Button onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={!canSave || saving}
                    startIcon={saving ? <CircularProgress size={16} /> : undefined}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
